import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { format, parse } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css'; // Custom styles
import calendarIcon from '../../assets/images/calendar.png';
import clearIcon from '../../assets/images/close_red.png';

interface DatePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  className?: string;
  label?: string;
  showLabel?: boolean;
  min?: string;
  max?: string;
  type?: 'date' | 'datetime-local';
  disabled?: boolean;
  placeholder?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  required = false,
  className = '',
  label = 'Date & Time',
  showLabel = true,
  min,
  max,
  type = 'datetime-local',
  disabled = false,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectionStep, setSelectionStep] = useState<'date' | 'time'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  // Convert string value to Date object
  const dateValue = value && value.trim() !== '' ? new Date(value) : null;
  
  // Check if date is valid
  const isValidDate = dateValue && !isNaN(dateValue.getTime());

  const handleDateSelection = (date: Date | null) => {
    if (!date) return;
    
    setSelectedDate(date);
    
    if (type === 'datetime-local') {
      // Move to time selection step - don't call onChange yet
      setSelectionStep('time');
      // Keep calendar open for time selection
    } else {
      // For date-only selection, we're done
      const formattedDate = format(date, 'yyyy-MM-dd');
      onChange(formattedDate);
      setIsOpen(false);
    }
  };

  const handleTimeSelection = (date: Date | null) => {
    if (!date || !selectedDate) return;
    
    // Combine selected date with selected time
    const combinedDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      date.getHours(),
      date.getMinutes()
    );
    
    const formattedDate = format(combinedDate, "yyyy-MM-dd'T'HH:mm");
    onChange(formattedDate);
    setIsOpen(false);
    setSelectionStep('date');
    setSelectedDate(null);
  };

  const handleDateChange = (date: Date | null) => {
    if (selectionStep === 'date') {
      handleDateSelection(date);
    } else {
      handleTimeSelection(date);
    }
  };

  // Clear button handler
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening calendar when clearing
    onChange('');
    setSelectedDate(null);
    setSelectionStep('date');
  };

  // Custom input component to match our styling
  const CustomInput = React.forwardRef<HTMLInputElement, any>(({ value, onClick, onChange: onInputChange }, ref) => (
    <div className="relative">
      <input
        ref={ref}
        type="text"
        value={getDisplayValue()}
        onClick={onClick}
        onChange={onInputChange}
        disabled={disabled}
        required={required}
        placeholder={placeholder || (type === 'date' ? 'Select date' : 'Select date and time')}
        className={`
          w-full px-4 py-2 rounded text-dark bg-bg_grey_light
          transition-colors duration-300 font-sora cursor-pointer
          pr-16 ${className}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          focus:outline-none focus:ring-1 focus:ring-primary focus:ring-opacity-50
          caret-primary font-mono text-[14px]
        `}
        style={{ caretColor: 'var(--primary)' }}
        readOnly // Make it read-only to prevent manual editing
      />
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-danger hover:text-danger_dark transition-colors duration-200 p-1 rounded-full hover:bg-bg_grey_light"
            title="Clear date"
            aria-label="Clear date"
          >
             <img src={clearIcon} alt="Clear" className="w-4 h-4 rotate-180" />
          </button>
        )}
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`
            text-grey_dark hover:text-dark p-1 rounded
            focus:outline-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          aria-label="Open calendar"
          tabIndex={disabled ? -1 : 0}
        >
          <img src={calendarIcon} alt="Calendar" className="w-4 h-4" />
        </button>
      </div>
    </div>
  ));
  
  CustomInput.displayName = 'CustomInput';
  
  // Determine date format based on type and current step
  const getDateFormat = () => {
    if (type === 'date') return 'yyyy-MM-dd';
    return selectionStep === 'date' ? 'MMMM d, yyyy' : 'h:mm aa';
  };

  const getShowTimeSelect = () => {
    return type === 'datetime-local' && selectionStep === 'time';
  };

  const getSelectedValue = () => {
    if (selectionStep === 'time' && selectedDate) {
      // For time selection step, use the selected date as base
      return selectedDate;
    }
    return isValidDate ? dateValue : null;
  };

  // Determine what value to display in the input field
  const getDisplayValue = () => {
    if (type === 'date') {
      return value || '';
    }
    
    // For datetime-local, format the value to be more user-friendly
    if (value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return format(date, "yyyy-MM-dd HH:mm:ss");
        }
      } catch (error) {
        // Fallback to original value if parsing fails
      }
    }
    return value || '';
  };

  return (
    <div className="w-full">
      {showLabel && (
        <label
          htmlFor={id}
          className="text-sm font-medium text-grey_dark mb-1 block"
        >
          {label} {required && <span className="text-danger_dark">*</span>}
        </label>
      )}
      
      <ReactDatePicker
        id={id}
        selected={getSelectedValue()}
        onChange={handleDateChange}
        customInput={<CustomInput />}
        showPopperArrow={false}
        popperClassName="react-datepicker-custom"
        wrapperClassName="w-full"
        dateFormat={getDateFormat()}
        showTimeSelect={getShowTimeSelect()}
        showTimeSelectOnly={selectionStep === 'time'}
        timeFormat="h:mm aa"
        timeIntervals={15}
        timeCaption="Select Time"
        disabled={disabled}
        required={required}
        open={isOpen}
        onCalendarOpen={() => {
          setIsOpen(true);
          setSelectionStep('date');
          setSelectedDate(isValidDate ? dateValue : null);
        }}
        onCalendarClose={() => {
          setIsOpen(false);
          // If we were in the middle of datetime-local selection and didn't complete it, reset
          if (type === 'datetime-local' && selectionStep === 'time' && selectedDate) {
            setSelectedDate(null);
            setSelectionStep('date');
          }
        }}
        placeholderText={placeholder || (type === 'date' ? 'Select date' : 'Select date and time')}
        calendarClassName="font-sora"
        className="w-full"
        shouldCloseOnSelect={selectionStep === 'date' && type === 'datetime-local' ? false : true}
        injectTimes={[]}
      />
    </div>
  );
};

export default DatePicker;